import { derived, get, writable, type Readable } from "svelte/store"
import type { BillingPlan, OrgBilling } from "@kontextwerk/control-web-client"
import { getAdminApi } from "../../../../../api/kehrblatt/client"
import {
  type KehrblattPlanCapabilities,
  resolveKehrblattPlanCapabilities,
} from "../org-billing-plan-capabilities"

export {
  type KehrblattPlanCapabilities,
  type KehrblattContactChannel,
  type KehrblattPlanFamily,
  planSupportsMobileMessage,
  planSupportsVoice,
  resolveKehrblattPlanCapabilities,
  resolveKehrblattPlanFamily,
} from "../org-billing-plan-capabilities"

export type OrgBillingPlanState = {
  billing: OrgBilling | null
  plan: BillingPlan | null
  loading: boolean
  error: string | null
  loadedAt: number | null
}

const initialState = (): OrgBillingPlanState => ({
  billing: null,
  plan: null,
  loading: false,
  error: null,
  loadedAt: null,
})

const normalizeOrgId = (orgId?: string | null) => (orgId || "").trim()
const inflight = new Map<string, Promise<OrgBillingPlanState>>()

export const orgBillingPlans = writable<Record<string, OrgBillingPlanState>>({})

const isFreshEnough = (state: OrgBillingPlanState | null | undefined, maxAgeMs?: number) => {
  if (!state || state.loading || state.loadedAt === null) return false

  if (maxAgeMs === undefined || maxAgeMs === null) return true

  return Date.now() - state.loadedAt <= maxAgeMs
}

type LoadBillingPlanOptions = {
  force?: boolean
  includePlan?: boolean
  maxAgeMs?: number
}

const inflightKey = (orgId: string, includePlan?: boolean) =>
  `${orgId}:${includePlan ? "with-plan" : "billing"}`

export const refreshOrgBillingPlan = async (
  orgId?: string | null,
  options?: Pick<LoadBillingPlanOptions, "includePlan">,
): Promise<OrgBillingPlanState | null> => {
  const id = normalizeOrgId(orgId)

  if (!id) return null

  const includePlan = options?.includePlan === true
  const key = inflightKey(id, includePlan)

  const existing = inflight.get(key)

  if (existing) return existing

  const promise = (async () => {
    orgBillingPlans.update((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? initialState()), loading: true, error: null },
    }))

    try {
      const billing = await getAdminApi().organization.billing.getOrgBilling(id)
      let plan: BillingPlan | null = null

      if (includePlan && billing?.plan_version_id) {
        try {
          plan = await getAdminApi().billing.getPlan(billing.plan_version_id)
        } catch (err) {
          console.error("org_plan_fetch_failed", err)
        }
      }

      const state: OrgBillingPlanState = {
        billing,
        plan,
        loading: false,
        error: null,
        loadedAt: Date.now(),
      }
      orgBillingPlans.update((prev) => ({ ...prev, [id]: state }))

      return state
    } catch (err) {
      console.error("org_billing_fetch_failed", err)
      const state: OrgBillingPlanState = {
        ...initialState(),
        loading: false,
        error: "Abrechnungsplan konnte nicht geladen werden.",
      }
      orgBillingPlans.update((prev) => ({ ...prev, [id]: state }))

      return state
    } finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, promise)

  return promise
}

export const ensureOrgBillingPlan = async (
  orgId?: string | null,
  options?: Pick<LoadBillingPlanOptions, "includePlan" | "maxAgeMs">,
): Promise<OrgBillingPlanState | null> => {
  const id = normalizeOrgId(orgId)

  if (!id) return null

  const includePlan = options?.includePlan === true
  const key = inflightKey(id, includePlan)
  const existing = inflight.get(key)

  if (existing) return existing

  const cached = get(orgBillingPlans)[id]

  if (
    isFreshEnough(cached, options?.maxAgeMs) &&
    (!includePlan || cached?.plan || !cached?.billing?.plan_version_id)
  ) {
    return cached
  }

  return refreshOrgBillingPlan(id, { includePlan })
}

export const loadOrgBillingPlan = async (
  orgId?: string | null,
  options?: LoadBillingPlanOptions,
): Promise<OrgBillingPlanState | null> => {
  return options?.force
    ? refreshOrgBillingPlan(orgId, { includePlan: options.includePlan })
    : ensureOrgBillingPlan(orgId, {
        includePlan: options?.includePlan,
        maxAgeMs: options?.maxAgeMs,
      })
}

export const orgPlanLookup: Readable<(orgId?: string | null) => OrgBillingPlanState | null> =
  derived(orgBillingPlans, ($plans) => {
    return (orgId?: string | null) => $plans[normalizeOrgId(orgId)] ?? null
  })

export const orgVoicePermission: Readable<(orgId?: string | null) => boolean> = derived(
  orgBillingPlans,
  ($plans) => {
    return (orgId?: string | null) => {
      const entry = $plans[normalizeOrgId(orgId)]

      return resolveKehrblattPlanCapabilities(entry).hasPhone
    }
  },
)

export const orgContactChannelCapabilities: Readable<
  (orgId?: string | null) => KehrblattPlanCapabilities["allowedContactChannels"]
> = derived(orgBillingPlans, ($plans) => {
  return (orgId?: string | null) => {
    const entry = $plans[normalizeOrgId(orgId)]

    return resolveKehrblattPlanCapabilities(entry).allowedContactChannels
  }
})

export const orgPlanCapabilities: Readable<(orgId?: string | null) => KehrblattPlanCapabilities> =
  derived(orgBillingPlans, ($plans) => {
    return (orgId?: string | null) => {
      const entry = $plans[normalizeOrgId(orgId)]

      return resolveKehrblattPlanCapabilities(entry)
    }
  })
